import { Component, computed, effect, inject, Input, signal, Signal } from '@angular/core';
import { AuthenticationService } from '@lib/auth/authentication.service';
import { PackageService } from '@lib/package/package.service';
import { ComponentsModule, FormFieldModule, SnackBarService, SuffixDirective } from '@mastechnicaegroup/carelinelive-ui';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, distinctUntilChanged, map, shareReplay, startWith, Subject, switchMap } from 'rxjs';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Result, toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { PaginatedApiResponse } from '@lib/api/responses/paginated-api-response';
import { postParamNamesFilterQuery } from '@shared/filters/filter-params';
import { PackageResponse } from '@lib/api/models/package';
import { AppDatePipe } from '@shared/pipes/date.pipe';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { PaginationCardComponent } from '@shared/pagination/pagination-card.component';

@Component({
  selector: 'app-dashboard-configurable-package-response-table',
    imports: [
        ErrorPanelComponent,
        FormFieldModule,
        PaginationCardComponent,
        ReactiveFormsModule,
        SuffixDirective,
        ComponentsModule,
    ],
  templateUrl: './dashboard-configurable-package-response-table.component.html',
  styleUrl: './dashboard-configurable-package-response-table.component.scss'
})
export class DashboardConfigurablePackageResponseTableComponent {
    @Input() config!: Signal<Record<string, string | number | null>>;
    @Input() columns!: { key: string, value: string }[];
    @Input() title!: string;
    @Input() subTitle!: string;

    private readonly _authService = inject(AuthenticationService);
    private readonly _packageService = inject(PackageService);
    private readonly _snackBar = inject(SnackBarService);

    protected user = toSignal(this._authService.user$);

    /** The current page being shown */
    protected readonly page = signal<number>(1);

    private config$ = new Subject<Record<string, string | number | null>>();

    /** The search form control */
    protected readonly searchControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });

    /** Fetch the page of packages */
    protected readonly pageResult: Signal<Result<PaginatedApiResponse<string[]>> | undefined> = toSignal(combineLatest({
        page: toObservable(this.page),
        // join the config observable with the search control changes observable
        queryParams: combineLatest([
            this.config$,
            this.searchControl.valueChanges.pipe(
                // start with the initial value
                startWith(this.searchControl.value),
                map(value => value.trim())
            )
        ]).pipe(
            map(([config, search]) => {
                const queryParams = { ...config };

                // if there's a search term, add it to the query params
                if (search) {
                    queryParams[postParamNamesFilterQuery] = search;
                }
                return queryParams;
            })
        )

    }).pipe(
        distinctUntilChanged((prev, cur) => {
            return JSON.stringify(prev) === JSON.stringify(cur);
        }),
        switchMap(({ page, queryParams }) => {
            // depending on the data source, call the relevant service method
            const responses$ = this._packageService.listProviderResponses(page, queryParams);

            return responses$.pipe(
                // some columns require transforming data so check the columns array for the object key values
                map((response) => {
                    // map values in the data rows if required
                    const mappedData = response.data.map((item) => {
                        // newItem is a PackageResponse but can have extra transformed fields
                        // the first field is always the package id so we can enable the click behaviour
                        const newItem: string[] = [];
                        if(
                            // only allow package_id for link id it's not assigned
                            // to a different provider
                            (item.package?.provider?.id ?? null) === null ||
                            item.package?.provider?.id === this.user()?.provider?.id
                        ){
                            newItem.push(item.package_id);
                        } else {
                            newItem.push('');
                        }
                        this.columns.forEach((column) => {
                            // map values that need transforming
                            switch (column.key) {
                                case 'reference': {
                                    newItem.push(item.package?.reference??'N/A');
                                    break;
                                }
                                case 'commissioner': {
                                    newItem.push(item.package?.commissioner?.name??'N/A');
                                    break;
                                }
                                case 'provider': {
                                    newItem.push(item.provider?.name??'N/A');
                                    break;
                                }
                                case 'post_code': {
                                    newItem.push(item.package?.address?.post_code??'N/A');
                                    break;
                                }
                                case 'status': {
                                    // determine status based on package properties
                                    let status = item.response ?? 'Unknown';
                                    if( !item.package?.provider?.id) {
                                        status = 'Available';
                                    } else if(item.package?.provider?.id !== item.provider?.id) {
                                        status = 'Unavailable';
                                    }
                                    // status in titlecase
                                    status = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
                                    newItem.push(status);
                                    break;
                                }
                                case 'commissioner-status': {
                                    // determine status based on package properties
                                    let status = item.response ?? 'Unknown';
                                    if(item.package?.provider === null) {
                                        status = 'Available';
                                    }
                                    // status in titlecase
                                    status = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
                                    newItem.push(status);
                                    break;
                                }
                                case 'updated_at': {
                                    const datePipe = new AppDatePipe();
                                    const formattedDate = item?.updated_at
                                        ? datePipe.transform(item.updated_at, 'dd/MM/y HH:mm')
                                        : 'N/A';
                                    newItem.push(formattedDate);
                                    break;
                                }
                                case 'comments': {
                                    // comments from offers or responses
                                    newItem.push(item.additional_comments ?? 'None');
                                    break;
                                }
                                default: {
                                    const value = item[column.key as keyof PackageResponse];
                                    newItem.push(value != null ? String(value) : 'N/A');
                                    break;
                                }
                            }

                        });
                        return newItem;
                    });

                    return { ...response, data: mappedData };
                }),
                // Turn into a Result
                toResult()
            );
        }),

        // Share the same result with all new subscribers
        shareReplay(1)
    ));

    /** The pagination meta data */
    protected readonly meta = computed(() => {
        // Extract the meta-data from the page result
        return this.pageResult()?.value?.meta;
    });

    constructor() {
        effect(() => {
            this.config$.next(this.config());
        });
    }

    protected nextPage(): void {
        const meta = this.meta();

        if (meta) {
            this.setPage(meta.current_page + 1);
        }
    }

    protected previousPage(): void {
        const meta = this.meta();

        if (meta) {
            this.setPage(meta.current_page - 1);
        }
    }

    protected async setPage(page: number) {
        this.page.set(page);
    }

    openPackageInNewTab(packageId: string | undefined) {
        if (!packageId) {
            this._snackBar.open({
                colour: 'error',
                icon: 'error_outline',
                title: 'Package not found'
            });
            return;
        }

        const url = `/packages/${packageId}?type=potential-packages`;
        window.open(url, '_blank');
    }
}
